import { EnvironmentInjector, Injectable, inject } from '@angular/core';
import { fromJson, toJson } from '@bufbuild/protobuf';
import { NbToastrService } from '@nebular/theme';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  CollectionNames,
  ProjectSurveyTaskServiceCreateResponseJson,
  ProjectSurveyTaskServiceCreateResponseSchema,
  ProjectSurveyTaskServiceListRequestJson,
  ProjectSurveyTaskServiceListRequestSchema,
  defaultApiSerializeJsonOptions,
} from '@vmspdb/definitions';
import { GlobalErrorHandlerService } from '@vmspdb/ui/common';
import { AuthRunEffects } from '@vmspdb/ui/store';
import { EMPTY, Observable, of, tap } from 'rxjs';
import { catchError, debounceTime, map, switchMap } from 'rxjs/operators';
import {
  SurveyTaskRepositoryService,
  withSearchSync,
} from '../../repositories';
import { UtilitiesFacade } from '../utilities/utilities.facade';
import { PaginatedSurveyTaskActions } from './paginated-survey-task.actions';

@Injectable()
export class PaginatedSurveyTaskEffects extends AuthRunEffects {
  private readonly injector = inject(EnvironmentInjector);
  private readonly errors = inject(GlobalErrorHandlerService);
  private readonly repository = inject(SurveyTaskRepositoryService);
  private readonly notifications = inject(NbToastrService);
  private readonly utilities = inject(UtilitiesFacade);

  listPaginatedSurveyTasks$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        PaginatedSurveyTaskActions.listPaginatedSurveyTasks,
        PaginatedSurveyTaskActions.cancel,
      ),
      switchMap((action) =>
        action.type === PaginatedSurveyTaskActions.cancel.type
          ? EMPTY
          : of(action).pipe(this.performSearch()),
      ),
    );
  });

  performSearch() {
    return (
      source: Observable<{
        params: ProjectSurveyTaskServiceListRequestJson;
      }>,
    ) =>
      source.pipe(
        debounceTime(250),
        withSearchSync(CollectionNames.Surveys, this.injector),
        tap(() => this.utilities.queue()),
        switchMap((payload) => {
          return this.repository.list(
            fromJson(ProjectSurveyTaskServiceListRequestSchema, payload.params),
          );
        }),
        tap((data) => {
          if (data.degraded) {
            this.errors.reportSearchDegradation(CollectionNames.Surveys);
            this.notifications.warning(
              'There was an error with the search server for fetching survey tasks.',
              'Search',
              {
                duration: 5000,
              },
            );
          }
        }),
        map((data) =>
          PaginatedSurveyTaskActions.listPaginatedSurveyTasksSuccess({
            data: {
              ...data,
              items: (data.items ?? []).map(
                (item) =>
                  toJson(
                    ProjectSurveyTaskServiceCreateResponseSchema,
                    item,
                    defaultApiSerializeJsonOptions,
                  ) as ProjectSurveyTaskServiceCreateResponseJson,
              ),
            },
          }),
        ),
        catchError((error) => {
          this.errors.reportCaughtError(error);
          return of(
            PaginatedSurveyTaskActions.listPaginatedSurveyTasksFailure(error),
          );
        }),
        tap(() => this.utilities.complete()),
      );
  }

  constructor() {
    super(inject(Actions));
  }
}
